
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StudentWithAttendance } from "@/hooks/useReportsData";
import { LateRecord } from "@/hooks/useLateAttendance";
import { AlertSettings } from "@/types/alertSettings";
import { useRealSchoolSettings } from "@/hooks/useRealSchoolSettings";
import { toArabicNumbers, formatDateWithArabicNumbers } from "@/utils/arabicNumbers";
import { getVisiblePaginationPages } from "@/utils/getVisiblePaginationPages";

interface LateAttendanceTableProps {
  studentsWithAttendance: StudentWithAttendance[];
  lateRecords: LateRecord[];
  enabledStages?: any[];
}



const calculateTotalLateDays = (lateRecords: LateRecord[], studentId: string): number => {
  return lateRecords.filter(record => record.studentId === studentId).length;
};

const calculateConsecutiveLateDays = (lateRecords: LateRecord[], studentId: string): number => {
  if (!lateRecords || lateRecords.length === 0) {
    return 0;
  }

  const studentLateRecords = lateRecords.filter(record => record.studentId === studentId);
  if (studentLateRecords.length === 0) {
    return 0;
  }

  studentLateRecords.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  let consecutiveDays = 0;
  let previousDate: Date | null = null;

  for (const record of studentLateRecords) {
    const currentDate = new Date(record.date);

    if (!previousDate) {
      consecutiveDays = 1;
      previousDate = currentDate;
    } else {
      const timeDifference = previousDate.getTime() - currentDate.getTime();
      const dayDifference = timeDifference / (1000 * 60 * 60 * 24);

      if (dayDifference === 1) {
        consecutiveDays++;
        previousDate = currentDate;
      } else {
        break;
      }
    }
  }

  return consecutiveDays;
};

// دالة لتحديد لون حالة التأخر بناءً على إعدادات التنبيهات
const getLateStatusColor = (lateDays: number, alertSettings?: AlertSettings | any): string => {
  let processedAlertSettings: AlertSettings | undefined;
  
  if (alertSettings) {
    if (typeof alertSettings === 'string') {
      try {
        processedAlertSettings = JSON.parse(alertSettings);
      } catch (error) {
        console.error('خطأ في تحليل alertSettings:', error);
        processedAlertSettings = undefined;
      }
    } else if (alertSettings.late_alerts) {
      processedAlertSettings = alertSettings;
    } else {
      processedAlertSettings = undefined;
    }
  }

  // إذا لم تكن هناك إعدادات تنبيهات تأخر، استخدم الألوان الافتراضية
  if (!processedAlertSettings?.late_alerts) {
    if (lateDays === 0) {
      return "text-emerald-600 bg-emerald-50 border-emerald-200";
    } else if (lateDays <= 2) {
      return "text-amber-600 bg-amber-50 border-amber-200";
    } else if (lateDays <= 4) {
      return "text-orange-600 bg-orange-50 border-orange-200";
    } else {
      return "text-red-600 bg-red-50 border-red-200";
    }
  }

  const enabledLateAlerts = processedAlertSettings.late_alerts
    .filter(alert => alert.enabled)
    .sort((a, b) => a.threshold - b.threshold);

  if (enabledLateAlerts.length === 0) {
    return "text-emerald-600 bg-emerald-50 border-emerald-200";
  }

  // تحديد مستوى التنبيه بناءً على عدد أيام التأخر
  let alertLevel = 0;
  for (const alert of enabledLateAlerts) {
    if (lateDays >= alert.threshold) {
      alertLevel++;
    }
  }

  // تطبيق الألوان بناءً على مستوى التنبيه
  if (alertLevel === 0) {
    return "text-emerald-600 bg-emerald-50 border-emerald-200";
  } else if (alertLevel === 1) {
    return "text-amber-600 bg-amber-50 border-amber-200";
  } else if (alertLevel === 2) {
    return "text-orange-600 bg-orange-50 border-orange-200";
  } else if (alertLevel === 3) {
    return "text-red-600 bg-red-50 border-red-200";
  } else {
    return "text-red-800 bg-red-100 border-red-300";
  }
};

// دالة لتحديد نص حالة التأخر بناءً على إعدادات التنبيهات
const getLateStatusText = (lateDays: number, alertSettings?: AlertSettings | any): string => {
  let processedAlertSettings: AlertSettings | undefined;
  
  if (alertSettings) {
    if (typeof alertSettings === 'string') {
      try {
        processedAlertSettings = JSON.parse(alertSettings);
      } catch (error) {
        console.error('خطأ في تحليل alertSettings:', error);
        processedAlertSettings = undefined;
      }
    } else if (alertSettings.late_alerts) {
      processedAlertSettings = alertSettings;
    } else {
      processedAlertSettings = undefined;
    }
  }

  // إذا لم تكن هناك إعدادات تنبيهات تأخر، استخدم النصوص الافتراضية
  if (!processedAlertSettings?.late_alerts) {
    if (lateDays === 0) {
      return "طبيعي";
    } else if (lateDays <= 2) {
      return "تنبيه أول";
    } else if (lateDays <= 4) {
      return "تنبيه ثاني";
    } else {
      return "تنبيه أخير";
    }
  }

  const enabledLateAlerts = processedAlertSettings.late_alerts
    .filter(alert => alert.enabled)
    .sort((a, b) => a.threshold - b.threshold);

  if (enabledLateAlerts.length === 0 || lateDays === 0) {
    return "طبيعي";
  }

  // البحث عن أعلى تنبيه يتطابق مع عدد أيام التأخر
  let matchedAlert = null;
  for (const alert of enabledLateAlerts) {
    if (lateDays >= alert.threshold) {
      matchedAlert = alert;
    }
  }

  return matchedAlert ? matchedAlert.title : "طبيعي";
};

export const LateAttendanceTable = ({ 
  studentsWithAttendance, 
  lateRecords,
}: LateAttendanceTableProps) => {
  // console.log('LateAttendanceTable studentsWithAttendance: ', studentsWithAttendance);
  // console.log('LateAttendanceTable lateRecords: ', lateRecords);
  const { settings } = useRealSchoolSettings();
  const [studentDetails, setStudentDetails] = useState<{ student: StudentWithAttendance | null, lateRecords: LateRecord[] }>({ student: null, lateRecords: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 18;

  const filteredData: {
    student: StudentWithAttendance;
    lateRecords: LateRecord[];
    totalLateDays: number;
    consecutiveLateDays: number;
  }[] = useMemo(() => {
    return studentsWithAttendance
      .map(student => {
        const studentLateRecords = lateRecords.filter(record => record.studentId === student.id);
        const totalLateDays = calculateTotalLateDays(lateRecords, student.id);
        const consecutiveLateDays = calculateConsecutiveLateDays(lateRecords, student.id);

        return {
          student: student,
          lateRecords: studentLateRecords,
          totalLateDays: totalLateDays,
          consecutiveLateDays: consecutiveLateDays
        };
      });
  }, [studentsWithAttendance, lateRecords]);

  // حساب pagination
  const totalPages = Math.ceil(filteredData.length / studentsPerPage);
  const startIndex = (currentPage - 1) * studentsPerPage;
  const endIndex = startIndex + studentsPerPage;
  const currentPageData = filteredData.slice(startIndex, endIndex);




  return (
    <div className="space-y-6">
      <Card className="bg-white shadow-lg border-0 rounded-xl overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-secondary/5 to-secondary/10 border-b border-secondary/20 py-6">
          <div className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-bold text-foreground flex items-center gap-3">
              <div className="w-1 h-8 bg-secondary rounded-full"></div>
              تقرير التأخر المفصل
            </CardTitle>
            <div className="text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-lg">
              عرض {toArabicNumbers(startIndex + 1)}-{toArabicNumbers(Math.min(endIndex, filteredData.length))} من {toArabicNumbers(filteredData.length)} طالب
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div id="late-attendance-table" className="overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-primary-teal to-primary-teal hover:from-primary-teal hover:to-primary-teal border-0">
                    <TableHead className="text-white font-bold text-center py-4 px-6 text-sm border-r border-slate-600 last:border-r-0">م</TableHead>
                    <TableHead className="text-white font-bold text-right py-4 px-6 text-sm border-r border-slate-600 min-w-[200px]">اسم الطالب</TableHead>
                    <TableHead className="text-white font-bold text-center py-4 px-6 text-sm border-r border-slate-600">عدد أيام التأخر</TableHead>
                    <TableHead className="text-white font-bold text-center py-4 px-6 text-sm border-r border-slate-600">عدد أيام التأخر المتتالية</TableHead>
                    <TableHead className="text-white font-bold text-center py-4 px-6 text-sm border-r border-slate-600">نسبة الانضباط</TableHead>
                    <TableHead className="text-white font-bold text-center py-4 px-6 text-sm">الحالة</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentPageData.map((item, index) => (
                    <TableRow 
                      key={item.student.id}
                      className={`
                        ${(startIndex + index) % 2 === 0 
                          ? 'bg-slate-50/50' 
                          : 'bg-white'
                        } 
                        hover:bg-blue-50/70 transition-all duration-200 border-b border-slate-100 last:border-b-0
                        ${item.consecutiveLateDays >= 3 ? 'bg-red-50/30' : ''}
                      `}
                    >
                      <TableCell className="text-center font-bold text-slate-700 py-2.5 px-6 border-r border-slate-100 last:border-r-0">
                        {toArabicNumbers(startIndex + index + 1)}
                      </TableCell>
                      <TableCell className="font-semibold text-slate-800 py-2.5 px-6 border-r border-slate-100 text-right">
                        {item.student.name}
                      </TableCell>
                      <TableCell className="text-center py-2.5 px-6 border-r border-slate-100">
                        <div className="flex justify-center">
                          <span className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 ${
                            item.totalLateDays === 0 
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : item.totalLateDays <= 2
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : item.totalLateDays <= 4
                              ? 'bg-orange-50 text-orange-700 border-orange-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                          }`}>
                            {toArabicNumbers(item.totalLateDays)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center py-2.5 px-6 border-r border-slate-100">
                        <div className="flex justify-center">
                          <span className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 ${
                            item.consecutiveLateDays === 0 
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : item.consecutiveLateDays <= 2
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : item.consecutiveLateDays <= 4
                              ? 'bg-orange-50 text-orange-700 border-orange-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                          }`}>
                            {toArabicNumbers(item.consecutiveLateDays)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center py-2.5 px-6 border-r border-slate-100">
                        <div className="flex justify-center">
                          <span className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 ${
                            item.totalLateDays === 0 
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : item.totalLateDays <= 2
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : item.totalLateDays <= 4
                              ? 'bg-orange-50 text-orange-700 border-orange-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                          }`}>
                            {(() => {
                              // حساب نسبة الانضباط = 100 - (نسبة التأخر من المدة الإجمالية)
                              // افتراض أن المدة الإجمالية هي 30 يوم (يمكن تعديلها حسب الحاجة)
                              const totalDays = 30;
                              const disciplineRate = Math.max(0, Math.round(((totalDays - item.totalLateDays) / totalDays) * 100));
                              return `${toArabicNumbers(disciplineRate)}%`;
                            })()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center py-2.5 px-6">
                        <div className="flex justify-center">
                          <span className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 ${
                            getLateStatusColor(item.totalLateDays, settings?.alert_rules)
                          }`}>
                            {getLateStatusText(item.totalLateDays, settings?.alert_rules)}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination - Same as StudentsTable */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-t border-slate-200">
              <div className="text-sm text-slate-600">
                عرض {toArabicNumbers(startIndex + 1)} إلى {toArabicNumbers(Math.min(endIndex, filteredData.length))} من {toArabicNumbers(filteredData.length)} طالب
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm"
                >
                  السابق
                </Button>
                <div className="flex gap-1">
                  {getVisiblePaginationPages(currentPage, totalPages, 5).map(
                    (pageNum) => (
                      <Button
                        key={pageNum}
                        variant={
                          currentPage === pageNum ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="px-3 py-1 text-sm min-w-[32px]"
                      >
                        {toArabicNumbers(pageNum)}
                      </Button>
                    ),
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm"
                >
                  التالي
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {studentDetails.student && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="relative bg-white rounded-xl shadow-2xl p-8 w-full max-w-lg mx-4 border-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-secondary rounded-full"></div>
                <h2 className="text-xl font-bold text-slate-800">تفاصيل تأخر الطالب: {studentDetails.student.name}</h2>
              </div>
              
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {studentDetails.lateRecords.map((record, index) => (
                  <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-slate-700">التاريخ:</span>
                      <span className="text-slate-600">{formatDateWithArabicNumbers(new Date(record.date))}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-2">
                      <span className="font-medium text-slate-700">النوع:</span>
                      <span className="text-slate-600">{record.lateType}</span>
                    </div>
                    {record.time && (
                      <div className="flex justify-between items-center text-sm mt-2">
                        <span className="font-medium text-slate-700">الوقت:</span>
                        <span className="text-slate-600">{record.time}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <Button 
                variant="outline" 
                className="absolute top-4 left-4 text-slate-600 hover:text-slate-800 hover:bg-slate-100 border-slate-300 rounded-lg px-3 py-1.5" 
                onClick={() => setStudentDetails({ student: null, lateRecords: [] })}
              >
                إغلاق
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
